import { NewProduct, NewProductTag } from '@/types/api/api-v4/product'

type Filters = Record<string, number>

type Props = {
  products: NewProduct[]
}

export const useProductFilters = ({ products }: Props) => {
  const { $metrics } = useNuxtApp()

  // Флаг, для понимания, включен ли какой- либо фильтр
  const isFiltersActivated = ref(false)
  // Список примененных фильтров
  const selectedFilters = ref<Filters>({})
  // Список возможных фильтров на основе отфильтрованных товаров и примененных фильтров
  const possibleFilters = ref<Filters>({})

  const allProducts = computed(() => {
    return !isFiltersActivated.value ? products : filteredProducts.value
  })

  const setActiveFilter = (filter: NewProductTag | Partial<NewProductTag>) => {
    // Если нет slug-а у фильтра - выходим
    if (!filter.slug) return

    // Ставим флаг активированых фильтров
    isFiltersActivated.value = true

    // Складываем в объект ключом slug фильтра
    selectedFilters.value[filter.slug] = 1

    setPossibleFilters()

    $metrics?.eventSendGoal({ ym: 'add_filter' })
  }

  const deactivateFilter = (filter: NewProductTag) => {
    // Если нет slug-а у фильтра - выходим
    if (!filter.slug) return

    // Удаляем ключ у активных фильтров
    delete selectedFilters.value[filter.slug]

    // Если текущее состояние фильтров пустое - очищаем возможные фильтры
    if (Object.keys(selectedFilters.value).length === 0) {
      resetAllFilters()

      return
    }

    setPossibleFilters()
  }

  const filteredProducts = computed(() => {
    if (!isFiltersActivated.value) return

    return getFilteredProducts(products, selectedFilters.value)
  })

  // Получение продуктов по примененным фильтрам
  const getFilteredProducts = (products: NewProduct[], filters: Filters) => {
    const filtersLength = Object.keys(filters).length

    // если нет активных фильтров возвращаем все продукты
    if (!filtersLength) return products

    return products.filter((product: NewProduct) => {
      const tags = getProductTags(product)

      // если тегов нет, отбрасываем продукт
      if (!tags.length) return false
      // счетчик пересечений
      let intersections = 0
      // ищем пересечения
      for (const tag of tags) {
        if (filters[tag.slug]) intersections++
      }

      // Проверяем кол-во пересечений с кол-во активных фильтров
      return intersections === filtersLength
    })
  }

  const getPossibleFilters = (
    filteredProducts: NewProduct[] | undefined,
    selectedFilters: Filters,
  ): Filters => {
    if (!filteredProducts) return {}

    const possibleFilters: Filters = {}
    // Получаем массив всех тегов (включая дубликаты)
    const productTags = filteredProducts.flatMap((product) => {
      return getProductTags(product)
    })

    // Проходимся по тегам и сохраняем возможные фильтры, исключая дубликаты и текущие примененные фильтры
    for (const tag of productTags) {
      if (tag.slug in selectedFilters || tag.slug in possibleFilters) continue

      possibleFilters[tag.slug] = 1
    }

    return possibleFilters
  }

  /*
  Расчитываем возможные фильтры (Возможными фильтрами называем те фильтры,
  которые не являются активными, но все еще могут быть активированы, т.к попадают под пересечения фильтров продуктов)
*/
  const setPossibleFilters = () => {
    possibleFilters.value = getPossibleFilters(
      filteredProducts.value,
      selectedFilters.value,
    )
  }

  const resetAllFilters = () => {
    isFiltersActivated.value = false
    selectedFilters.value = {}
    possibleFilters.value = {}
  }

  const getProductTags = (product: NewProduct) => {
    const tags = [...product.main_tags, ...product.additional_tags]

    if (!tags?.length) return []

    return tags
  }

  return {
    isFiltersActivated,
    selectedFilters,
    possibleFilters,
    allProducts,
    filteredProducts,
    setActiveFilter,
    resetAllFilters,
    setPossibleFilters,
    deactivateFilter,
  }
}
